import {
  getErrorState,
  getLoadingState,
  getSuccessState,
} from '@automata/utils';
import { useCallback } from 'react';

import { WorkflowSaveQueryResult, WorkflowSaveResult } from './types';
import { useCreateWorkflow, useUpdateWorkflow } from '../apis/wfbApiComponents';
import { WorkflowConfigInput } from '../apis/wfbApiSchemas';

type Options = Parameters<typeof useCreateWorkflow>[0];

export const useSaveWorkflow = (options: Options = {}): WorkflowSaveResult => {
  const {
    mutate: createWorkflowMutate,
    error: createWorkflowError,
    data: createWorkflowData,
    status: createWorkflowStatus,
  } = useCreateWorkflow(options);
  const {
    mutate: updateWorkflowMutate,
    error: updateWorkflowError,
    data: updateWorkflowData,
    status: updateWorkflowStatus,
  } = useUpdateWorkflow(options);

  // TODO(AL, 10/07/2024): fix the fact that this is always loading in an idle state.

  const saveWorkflow = useCallback(
    (workflowConfigInput: WorkflowConfigInput, workflowId?: string) => {
      if (workflowId) {
        updateWorkflowMutate({
          body: workflowConfigInput,
          pathParams: { workflowId },
        });
      } else {
        createWorkflowMutate({ body: workflowConfigInput });
      }
    },
    [createWorkflowMutate, updateWorkflowMutate]
  );

  const makeReturnData = useCallback(
    (result: WorkflowSaveQueryResult): WorkflowSaveResult => ({
      result,
      isIdle: [createWorkflowStatus, updateWorkflowStatus].every(
        (status) =>
          status === 'idle' || status === 'error' || status === 'success'
      ),
      saveWorkflow,
    }),
    [createWorkflowStatus, saveWorkflow, updateWorkflowStatus]
  );

  if (createWorkflowError) {
    return makeReturnData(
      getErrorState({
        errorType: 'createWorkflow',
        error: createWorkflowError,
      })
    );
  }

  if (updateWorkflowError) {
    return makeReturnData(
      getErrorState({
        errorType: 'updateWorkflow',
        error: updateWorkflowError,
      })
    );
  }

  if (createWorkflowData) {
    return makeReturnData(getSuccessState(createWorkflowData));
  }

  if (updateWorkflowData) {
    return makeReturnData(getSuccessState(updateWorkflowData));
  }

  return makeReturnData(getLoadingState());
};
